import { H } from "@highlight-run/next/client";
import { Typography } from "@mui/material";
import { useRouter } from "next/router";
import React, { ReactNode, useContext } from "react";
import { CombinedError } from "urql";

import { Redirect } from "../../components/Redirect";
import { CustomerContext } from "../../contexts/customer/CustomerContext";
import { FeatureFlagsContextProvider } from "../../contexts/feature-flags/FeatureFlagsContext";
import { PermissionsContextProvider } from "../../contexts/permission/PermissionsContext";
import { IUseOperationContextOpts, useOperationContext } from "../../hooks/useOperationContext";
import { Routes } from "../../Routes";
import { UserAnalyticsServicesProvider } from "../../services/UserAnalyticsServicesProvider";
import { visitGQLUnion } from "../../utils/visitGQLUnion";
import { visitQueryState } from "../../utils/visitQueryState";
import { FullPageError } from "../ui/layout/FullPageError";
import { OmiLoading } from "../ui/layout/OmiLoading";
import { ICurrentOmiUserProfileQuery, useCurrentOmiUserProfileQuery } from "./OmiAuthContextProvider.generated";
import { OmiUserContextProvider } from "./OmiUserContext";

const OMI_OPERATION_CONTEXT_OPTS: IUseOperationContextOpts<ICurrentOmiUserProfileQuery> = {
    additionalTypenameFlags: { DefaultError: false, User: true, Contract: true }, // Tier quota usage is impacted by creation/deletion of users/contracts.
};

export const OmiAuthenticatedContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { pathname } = useRouter();
    const [queryState] = useCurrentOmiUserProfileQuery({
        context: useOperationContext(OMI_OPERATION_CONTEXT_OPTS),
    });
    const { selectedCustomer } = useContext(CustomerContext);

    return visitQueryState<ICurrentOmiUserProfileQuery, ReactNode>(queryState, {
        notLoaded: () => <OmiLoading />,
        loaded: ({ data }) => renderLoaded(data),
        error: ({ error }) => renderError(error),
    });

    function renderLoaded({
        liteGetCurrentUserProfile: { userProfile, customer },
        getUserPermissions: { permissions },
        getSheetsTierSettings: { tierSettings },
        getOmiUserProfile: { contractsEmailAddress },
        getActiveFeatures: activeFeatureFlags,
    }: ICurrentOmiUserProfileQuery) {
        if (userProfile == null) {
            return renderError();
        }

        return visitGQLUnion<typeof userProfile, React.ReactElement>(userProfile, {
            User: (user) => (
                <UserAnalyticsServicesProvider role={undefined} orgId={selectedCustomer?.id}>
                    <OmiUserContextProvider
                        omiUser={user}
                        omiCustomer={customer ?? undefined}
                        omiTierSettings={tierSettings ?? undefined}
                        omiContractsEmailAddress={contractsEmailAddress ?? undefined}
                    >
                        <PermissionsContextProvider permissions={permissions}>
                            <FeatureFlagsContextProvider activeFeatureFlags={activeFeatureFlags}>
                                {children}
                            </FeatureFlagsContextProvider>
                        </PermissionsContextProvider>
                    </OmiUserContextProvider>
                </UserAnalyticsServicesProvider>
            ),
            UserNotSignedUp: () =>
                isAccessibleWithoutOnboarding(pathname) ? (
                    <UserAnalyticsServicesProvider role={undefined} orgId={undefined}>
                        <OmiUserContextProvider
                            omiUser={undefined}
                            omiCustomer={undefined}
                            omiTierSettings={undefined}
                            omiContractsEmailAddress={undefined}
                        >
                            {children}
                        </OmiUserContextProvider>
                    </UserAnalyticsServicesProvider>
                ) : (
                    <Redirect to={Routes.Omi.Signup()} />
                ),
            unknown: () => renderError(),
        });
    }

    function renderError(error?: CombinedError) {
        if (error) {
            H.consumeError(error);
        }

        return (
            <FullPageError>
                <Typography>Error loading account information. Please refresh the page, or try again later.</Typography>
            </FullPageError>
        );
    }
};

export const OmiUnauthenticatedContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    return (
        <OmiUserContextProvider
            omiUser={undefined}
            omiCustomer={undefined}
            omiTierSettings={undefined}
            omiContractsEmailAddress={undefined}
        >
            {children}
        </OmiUserContextProvider>
    );
};

function isAccessibleWithoutOnboarding(pathname: string) {
    return pathname.startsWith(Routes.Omi.Signup());
}
